import { Injectable, NgZone } from "@angular/core";
import { Subject } from "rxjs";
import { SessionProvider } from "./session/session";
import { HttpClient } from "@angular/common/http";
import { AppDetectorProvider } from "./detector/detector";
import { Cache } from "../assets/data/cache-key/cache-key";

declare var BackgroundGeolocation, Location;

@Injectable()
export class TrackingService {
  isTracking: boolean = false;
  activeId: number = -1;
  mode: TrackingMode = TrackingMode.spectatorInvisible;
  onLocationSub = new Subject<any>();
  options: TrackingSettings;

  constructor(
    private sessionService: SessionProvider,
    private httpClient: HttpClient,
    private appDetector: AppDetectorProvider,
    private ngZone: NgZone
  ) {
    let settings = localStorage.getItem(Cache.TRACKING_SETTINGS);
    this.options = settings ? JSON.parse(settings) : {
      applause: false,
      autoPause: false,
      placement: false,
      google: false,
      locationExport: false,
      pluginAccuracy: PluginAccuracy.HIGH,
      lineWidth: TrackingLineWidth.TRACKING_LINE_NORMAL,
      distanceUnit: DistanceUnit.KM,
      trackingColor: this.detectDefaultColor()
    }
  }

  private detectDefaultColor() {
    if (this.appDetector.isForestApp()) {
      return TrackingLineColor.GREEN_COLOR;
    }
    return TrackingLineColor.BLUE_COLOR;
  }

  updateTrackingSettings() {
    localStorage.setItem(Cache.TRACKING_SETTINGS, JSON.stringify(this.options));
  }

  searchByCityName(lat, lng) {
    return new Promise((resolve, reject) => {
      let url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`;
      return fetch(url)
        .then(res => res.json())
        .then(res => resolve(res))
        .catch(err => {
          if (err.status === 400 || err.status === 401) {
            alert(err.error.message);
            resolve("")
          }
        });
    })
  }

  encodeRoute(route) {
    let encoded = '';
    let prevLat = 0;
    let prevLng = 0;

    for (const coord of route) {
      const lat = Math.round((coord.lat || coord[0]) * 1e5);
      const lng = Math.round((coord.lng || coord[1]) * 1e5);

      const dLat = lat - prevLat;
      const dLng = lng - prevLng;

      prevLat = lat;
      prevLng = lng;

      encoded += this.encodeValue(dLat) + this.encodeValue(dLng);
    }

    return encoded;
  }

  encodeValue(value) {
    let encoded = '';
    value = value < 0 ? ~(value << 1) : value << 1;

    while (value >= 0x20) {
      encoded += String.fromCharCode((0x20 | (value & 0x1f)) + 63);
      value >>= 5;
    }

    encoded += String.fromCharCode(value + 63);
    return encoded;
  }

  setupPlugin(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      if (!this.appDetector.isHannoverApp() || this.isTracking) {
        resolve(true);
        return;
      }

      this.sessionService.getToken(false, false).then(token => {
        console.log('setupPlugin() inside...');
        BackgroundGeolocation.onLocation(this.onLocation.bind(this));
        ////
        // Step 2:  Initialize the plugin
        //
        BackgroundGeolocation.ready({
          // Logging / Debug config
          url: "https://spooorts.de/route/user/location/save",
          // url: "http://localhost:8080/savelocation",
          headers: {
            "content-type": "application/json"
          },
          extras: {
            "token": token
            // "eventId": this.activeId
          },
          debug: false,
          logLevel: BackgroundGeolocation.LOG_LEVEL_ERROR,
          // Geolocation config
          desiredAccuracy: this.getPluginAccuracy(),
          distanceFilter: 5,
          // ActivityRecognition config
          stopTimeout: 1,
          reset: true,
          // Application config
          stopOnTerminate: false,
          stopOnStationary: false,
          heartbeatInterval: 60,
          disableElasticity: true,
          // [Android] backgroundPermissionRationale for Always permission.
          backgroundPermissionRationale: {
            title: "Ermöglichen Sie {applicationName}, auf den Speicherort dieses Geräts zuzugreifen, auch wenn es geschlossen ist oder nicht verwendet wird.",
            message: "Diese App verarbeitet Standortdaten, um das GPS Tracking zu ermöglichen.",
            positiveAction: 'Wechseln Sie zu "{backgroundPermissionOptionLabel}"',
            negativeAction: 'Abbrechen'
          },
          // HTTP / Persistence config
          notification: {
            smallIcon: "drawable/ic_notification"
          }
        }, (state) => {
          // Set current plugin state upon our view.
          this.ngZone.run(() => {
            BackgroundGeolocation.start(() => {
              this.isTracking = true;
              console.log('g-1');
              resolve(true);
              BackgroundGeolocation.changePace(true, () => {
                console.log('[Geolocation Service]::INFO, The plugin has started...');
                this.isTracking = true;
                resolve(true);
              }, (err) => {
                console.log('[Geolocation Service]::ERROR, changePace(): ' + err);
                this.isTracking = false;
                reject(false);
              });
            });
          });
        }).catch(err => {
          console.log('BackgroundGeolocation.ready err: ' + err);
          this.isTracking = false;
          reject(false)
        });
      });
    });
  }

  stopPlugin() {
    if (this.isTracking) {
      BackgroundGeolocation.stop();
      BackgroundGeolocation.reset();
      this.isTracking = false;
      this.removePos();
      console.log('the plugin has stopped...');
    }
  }

  onLocation(location: Location) {
    this.ngZone.run(() => {
      if (this.isTracking) {
        this.onLocationSub.next(location);
      }
    })
  }

  removePos() {
    console.log('[INFO] removePos()...');
    this.sessionService.getToken(false, false).then(token => {
      let url = `https://spooorts.de/route/user/${token}/location/delete`;
      this.httpClient.post(url, null).subscribe(() => {
      });
    }, (err) => {
      console.log('[HTTP remove pos] - failuer, ' + JSON.stringify(err));
    });
  }


  reconfigurePlugin() {
    let token = localStorage.getItem('token');
    console.log('token: ' + token);
    let eventId = localStorage.getItem('eventId');
    if (!this.options.locationExport) {
      BackgroundGeolocation.setConfig({
        autoSync: false,
        debug: false,
        logLevel: BackgroundGeolocation.LOG_LEVEL_ERROR,
        desiredAccuracy: this.getPluginAccuracy(),  // <-- highest possible accuracy
        distanceFilter: 5,
        stopTimeout: 1,
        reset: true,
        stopOnTerminate: false,
        stopOnStationary: false,
        maxBatchSize: 1,
        startOnBoot: true,
        heartbeatInterval: 60,
        disableElasticity: true,
        // [Android] backgroundPermissionRationale for Always permission.
        backgroundPermissionRationale: {
          title: "Ermöglichen Sie {applicationName}, auf den Speicherort dieses Geräts zuzugreifen, auch wenn es geschlossen ist oder nicht verwendet wird.",
          message: "Diese App verarbeitet Standortdaten, um das GPS Tracking zu ermöglichen.",
          positiveAction: 'Wechseln Sie zu "{backgroundPermissionOptionLabel}"',
          negativeAction: 'Abbrechen'
        },
        // HTTP / Persistence config
        notification: {
          smallIcon: "drawable/ic_notification"
        }
      })
    } else {
      BackgroundGeolocation.setConfig({

        url: "https://spooorts.de/route/user/location/save",
        // url: "http://localhost:8080/savelocation",
        headers: {
          "content-type": "application/json"
        },
        locationTemplate: '{"latitude":<%= latitude %>,"longitude":<%= longitude %>}',
        extras: {
          "token": token
          // "eventId": eventId
        },
        autoSync: true,
        batchSync: true,
        autoSyncThreshold: 10,
        debug: false,
        logLevel: BackgroundGeolocation.LOG_LEVEL_ERROR,
        desiredAccuracy: this.getPluginAccuracy(),  // <-- highest possible accuracy
        distanceFilter: 5,
        stopTimeout: 1,
        reset: true,
        stopOnTerminate: false,
        stopOnStationary: false,
        startOnBoot: true,
        heartbeatInterval: 60,
        disableElasticity: true,
        // [Android] backgroundPermissionRationale for Always permission.
        backgroundPermissionRationale: {
          title: "Ermöglichen Sie {applicationName}, auf den Speicherort dieses Geräts zuzugreifen, auch wenn es geschlossen ist oder nicht verwendet wird.",
          message: "Diese App verarbeitet Standortdaten, um das GPS Tracking zu ermöglichen.",
          positiveAction: 'Wechseln Sie zu "{backgroundPermissionOptionLabel}"',
          negativeAction: 'Abbrechen'
        },
        // HTTP / Persistence config
        notification: {
          smallIcon: "drawable/ic_notification"
        }
      });
    }
  }

  setPluginAccuracy() {
    let accuracy = this.getPluginAccuracy();
    BackgroundGeolocation.setConfig({
      desiredAccuracy: accuracy
    });
  }

  getPluginAccuracy() {
    let accuracy = BackgroundGeolocation.DESIRED_ACCURACY_HIGH;
    switch (this.options.pluginAccuracy) {
      case PluginAccuracy.LOW: accuracy = BackgroundGeolocation.DESIRED_ACCURACY_LOW; break;
      case PluginAccuracy.MEDIUM: accuracy = BackgroundGeolocation.DESIRED_ACCURACY_MEDIUM; break;
      case PluginAccuracy.HIGH: accuracy = BackgroundGeolocation.DESIRED_ACCURACY_HIGH; break;
    };
    localStorage.setItem("accuracy", accuracy + "");
    return accuracy;
  }

  onRequestLocation() {
    return this.onLocationSub;
  }
}

export interface TrackingSettings {
  distanceUnit: DistanceUnit;
  applause: boolean;
  autoPause: boolean;
  placement: boolean;
  google: boolean;
  locationExport: boolean;
  pluginAccuracy: PluginAccuracy;
  lineWidth: TrackingLineWidth;
  trackingColor: TrackingLineColor;
}
export enum PluginAccuracy {
  LOW,
  MEDIUM,
  HIGH
}
export enum TrackingLineColor {
  BLACK_COLOR,
  BLUE_COLOR,
  GREEN_COLOR,
  RED_COLOR
}
export enum TrackingLineWidth {
  TRACKING_LINE_SLIM,
  TRACKING_LINE_NORMAL,
  TRACKING_LINE_BROAD
}
export enum TrackingMode {
  spectatorInvisible,
  spectatorVisible,
  participant
}
export enum DistanceUnit {
  KM,
  MILES
}


// WEBPACK FOOTER //
// /src/providers/tracking.service.ts